import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { defaultResultViews, normalizePollType, PollSettings, PollType, SavedConfigurations } from "../pollTypes";
import { areConfigurationsEqual } from "../utils/pollConfigUtils";
import type { ConstantSumCluster, RankingOption } from "../pollTypes";

interface UsePollEditorDataProps {
  postId: string | null;
  pollId: string | null;
  activePollType: PollType;
  clusters: ConstantSumCluster[];
  hideZeroAllocated: boolean;
  rankingOptions: RankingOption[];
  minimumRequiredRanks: number;
  resultViews: Record<PollType, import("../pollTypes").PollResultView>;
  responseCount: number;
  baselinePollType: PollType | null;
  baselineConfigurations: SavedConfigurations;
  baselineSettings: {
    hideZeroAllocated: boolean;
    minimumRequiredRanks: number;
    resultViews: Record<PollType, import("../pollTypes").PollResultView>;
  } | null;
  setPollId: (value: string | null) => void;
  setActivePollType: (value: PollType) => void;
  setClusters: (value: ConstantSumCluster[]) => void;
  setHideZeroAllocated: (value: boolean) => void;
  setRankingOptions: (value: RankingOption[]) => void;
  setMinimumRequiredRanks: (value: number) => void;
  setSavedConfigurations: (value: SavedConfigurations) => void;
  setResultViews: (value: Record<PollType, import("../pollTypes").PollResultView>) => void;
  setResponseCount: (value: number) => void;
  setBaselinePollType: (value: PollType | null) => void;
  setBaselineConfigurations: (value: SavedConfigurations) => void;
  setBaselineSettings: (value: UsePollEditorDataProps["baselineSettings"]) => void;
  setLoading: (value: boolean) => void;
}

export const usePollEditorData = ({
  postId,
  pollId,
  activePollType,
  clusters,
  hideZeroAllocated,
  rankingOptions,
  minimumRequiredRanks,
  resultViews,
  responseCount,
  baselinePollType,
  baselineConfigurations,
  baselineSettings,
  setPollId,
  setActivePollType,
  setClusters,
  setHideZeroAllocated,
  setRankingOptions,
  setMinimumRequiredRanks,
  setSavedConfigurations,
  setResultViews,
  setResponseCount,
  setBaselinePollType,
  setBaselineConfigurations,
  setBaselineSettings,
  setLoading,
}: UsePollEditorDataProps) => {
  const loadPoll = useCallback(async () => {
    if (!postId) return;

    try {
      const { data: poll } = await supabase
        .from("polls")
        .select("*")
        .eq("blog_post_id", postId)
        .single();

      if (poll) {
        setPollId(poll.id);
        const settings = poll.settings as PollSettings | null;
        const savedConfigs = settings?.savedConfigurations || {};
        setSavedConfigurations(savedConfigs);
        setMinimumRequiredRanks(settings?.minimumRequiredRanks ?? 3);
        const resolvedViews = { ...defaultResultViews, ...(settings?.resultViews || {}) };
        setResultViews(resolvedViews);

        if (savedConfigs.constant_sum_two_level && "clusters" in savedConfigs.constant_sum_two_level) {
          const stored = savedConfigs.constant_sum_two_level as {
            clusters: ConstantSumCluster[];
            hideZeroAllocated?: boolean;
          };
          setClusters(stored.clusters || []);
          setHideZeroAllocated(stored.hideZeroAllocated ?? false);
        }

        if (savedConfigs.ranking && "options" in savedConfigs.ranking) {
          const storedRanking = savedConfigs.ranking as { options: RankingOption[] };
          setRankingOptions(storedRanking.options || []);
        }

        if (savedConfigs.ranking_minimum && "options" in savedConfigs.ranking_minimum) {
          const storedRanking = savedConfigs.ranking_minimum as { options: RankingOption[]; minimumRequired?: number };
          setRankingOptions(storedRanking.options || []);
          if (storedRanking.minimumRequired) {
            setMinimumRequiredRanks(storedRanking.minimumRequired);
          }
        }

        const resolvedType = normalizePollType(poll.poll_type);
        setActivePollType(resolvedType);
        setBaselinePollType(resolvedType);
        setBaselineConfigurations(savedConfigs);
        setBaselineSettings({
          hideZeroAllocated: settings?.hideZeroAllocated ?? false,
          minimumRequiredRanks: settings?.minimumRequiredRanks ?? 3,
          resultViews: resolvedViews,
        });

        const { data: clustersData } = await supabase
          .from("poll_clusters")
          .select("*")
          .eq("poll_id", poll.id)
          .order("display_order");

        if (clustersData) {
          if (resolvedType === "constant_sum_two_level") {
            const clustersWithSubs = await Promise.all(
              clustersData.map(async (cluster) => {
                const { data: subs } = await supabase
                  .from("poll_sub_categories")
                  .select("*")
                  .eq("cluster_id", cluster.id)
                  .order("display_order");

                return {
                  id: cluster.id,
                  label: cluster.label,
                  description: cluster.description || "",
                  image_url: cluster.image_url || "",
                  order: cluster.display_order,
                  subCategories: subs
                    ? subs.map((sub) => ({
                        id: sub.id,
                        label: sub.label,
                        description: sub.description || "",
                        order: sub.display_order,
                      }))
                    : [],
                } as ConstantSumCluster;
              }),
            );
            setClusters(clustersWithSubs);
            setHideZeroAllocated(settings?.hideZeroAllocated ?? false);
          } else {
            const options = clustersData.map((cluster) => ({
              id: cluster.id,
              label: cluster.label,
              description: cluster.description || "",
              image_url: cluster.image_url || "",
              order: cluster.display_order,
            }));
            setRankingOptions(options);
            if (resolvedType === "ranking_minimum") {
              setMinimumRequiredRanks(settings?.minimumRequiredRanks ?? Math.min(3, options.length || 3));
            }
          }
        }

        const { count } = await supabase
          .from("poll_responses")
          .select("*", { count: "exact", head: true })
          .eq("poll_id", poll.id);
        setResponseCount(count || 0);
      }
    } catch (error) {
      console.error("Error loading poll:", error);
      toast.error("Failed to load poll");
    }
  }, [
    postId,
    setPollId,
    setSavedConfigurations,
    setMinimumRequiredRanks,
    setResultViews,
    setClusters,
    setHideZeroAllocated,
    setRankingOptions,
    setActivePollType,
    setBaselinePollType,
    setBaselineConfigurations,
    setBaselineSettings,
    setResponseCount,
  ]);

  const savePoll = useCallback(
    async (postIdOverride?: string) => {
      const targetPostId = postIdOverride ?? postId;
      const hasPollConfiguration = Boolean(pollId) || clusters.length > 0 || rankingOptions.length > 0;

      if (!hasPollConfiguration) {
        return { status: "skipped" } as const;
      }

      if (!targetPostId) {
        toast.error("Cannot save poll without a post");
        return { status: "error" } as const;
      }
      if (activePollType === "constant_sum_two_level") {
        if (clusters.length === 0) {
          toast.error("Add at least one cluster");
          return { status: "invalid" } as const;
        }

        if (clusters.some((cluster) => !cluster.label.trim())) {
          toast.error("All clusters must have a label");
          return { status: "invalid" } as const;
        }
      }

      if (activePollType === "ranking" || activePollType === "ranking_minimum") {
        if (rankingOptions.length < 2) {
          toast.error("Add at least two options to create a ranking poll");
          return { status: "invalid" } as const;
        }

        if (rankingOptions.some((option) => !option.label.trim())) {
          toast.error("All ranking options must have a label");
          return { status: "invalid" } as const;
        }

        if (activePollType === "ranking_minimum") {
          if (minimumRequiredRanks < 1) {
            toast.error("Minimum required rankings must be at least 1");
            return { status: "invalid" } as const;
          }

          if (minimumRequiredRanks > rankingOptions.length) {
            toast.error("Minimum required rankings cannot exceed the number of options");
            return { status: "invalid" } as const;
          }
        }
      }

      setLoading(true);
      try {
        let currentPollId = pollId;
        const configurations: SavedConfigurations = {
          constant_sum_two_level: { clusters, hideZeroAllocated },
          ranking: { options: rankingOptions },
          ranking_minimum: { options: rankingOptions, minimumRequired: minimumRequiredRanks },
        };
        setSavedConfigurations(configurations);
        const settingsPayload: PollSettings = {
          hideZeroAllocated,
          minimumRequiredRanks,
          savedConfigurations: configurations,
          resultViews,
        };
        const pollTypeChanged = baselinePollType ? activePollType !== baselinePollType : Boolean(currentPollId);
        const configurationChanged = !areConfigurationsEqual(
          activePollType,
          configurations[activePollType],
          baselineConfigurations[activePollType],
        );
        const settingsChanged = baselineSettings
          ? hideZeroAllocated !== baselineSettings.hideZeroAllocated ||
            minimumRequiredRanks !== baselineSettings.minimumRequiredRanks ||
            JSON.stringify(resultViews) !== JSON.stringify(baselineSettings.resultViews)
          : Boolean(currentPollId);
        const requiresClusterReset = pollTypeChanged || configurationChanged;

        if (currentPollId && !requiresClusterReset && !settingsChanged) {
          return { status: "skipped" } as const;
        }

        if (!currentPollId) {
          const { data: newPoll, error: pollError } = await supabase
            .from("polls")
            .insert([
              {
                blog_post_id: targetPostId,
                poll_type: activePollType,
                settings: settingsPayload as unknown as import("@/integrations/supabase/types").Json,
              },
            ])
            .select()
            .single();

          if (pollError) throw pollError;
          currentPollId = newPoll.id;
          setPollId(currentPollId);
        } else {
          if (requiresClusterReset) {
            const { count: existingResponseCount } = await supabase
              .from("poll_responses")
              .select("*", { count: "exact", head: true })
              .eq("poll_id", currentPollId);

            if (existingResponseCount && existingResponseCount > 0) {
              const confirmWipe = window.confirm(
                `This poll has ${existingResponseCount} response(s). Saving poll changes will permanently delete all response data because cluster IDs will change. Click OK to save the poll changes and reset responses, or Cancel to keep the existing poll results and save only the blog post.`,
              );
              if (!confirmWipe) {
                setLoading(false);
                return { status: "skipped" } as const;
              }
              await supabase.from("poll_responses").delete().eq("poll_id", currentPollId);
            }
          }

          if (requiresClusterReset || settingsChanged || pollTypeChanged) {
            await supabase
              .from("polls")
              .update({
                poll_type: activePollType,
                settings: settingsPayload as unknown as import("@/integrations/supabase/types").Json,
              })
              .eq("id", currentPollId);
          }

          if (requiresClusterReset) {
            await supabase.from("poll_clusters").delete().eq("poll_id", currentPollId);
          }
        }

        if (requiresClusterReset) {
          if (activePollType === "constant_sum_two_level") {
            for (const cluster of clusters) {
              const { data: newCluster, error: clusterError } = await supabase
                .from("poll_clusters")
                .insert({
                  poll_id: currentPollId,
                  label: cluster.label,
                  description: cluster.description || null,
                  image_url: cluster.image_url || null,
                  display_order: cluster.order,
                })
                .select()
                .single();

              if (clusterError) throw clusterError;

              if (cluster.subCategories.length > 0) {
                const subsToInsert = cluster.subCategories.map((sub) => ({
                  cluster_id: newCluster.id,
                  label: sub.label,
                  description: sub.description || null,
                  display_order: sub.order,
                }));

                const { error: subsError } = await supabase.from("poll_sub_categories").insert(subsToInsert);

                if (subsError) throw subsError;
              }
            }
          } else {
            for (const option of rankingOptions) {
              const { error: clusterError } = await supabase.from("poll_clusters").insert({
                poll_id: currentPollId,
                label: option.label,
                description: option.description || null,
                image_url: option.image_url || null,
                display_order: option.order,
              });

              if (clusterError) throw clusterError;
            }
          }
        }

        toast.success("Poll saved successfully");
        await loadPoll();
        setBaselinePollType(activePollType);
        setBaselineConfigurations(configurations);
        setBaselineSettings({
          hideZeroAllocated,
          minimumRequiredRanks,
          resultViews,
        });
        return { status: "saved" } as const;
      } catch (error) {
        console.error("Error saving poll:", error);
        toast.error("Failed to save poll");
        return { status: "error" } as const;
      } finally {
        setLoading(false);
      }
    },
    [
      postId,
      pollId,
      clusters,
      rankingOptions,
      activePollType,
      hideZeroAllocated,
      minimumRequiredRanks,
      resultViews,
      baselinePollType,
      baselineConfigurations,
      baselineSettings,
      setSavedConfigurations,
      setPollId,
      setBaselinePollType,
      setBaselineConfigurations,
      setBaselineSettings,
      setLoading,
      loadPoll,
    ],
  );

  const wipePollData = useCallback(async () => {
    if (!pollId) return;

    setLoading(true);
    try {
      if (responseCount > 0) {
        const { error } = await supabase.from("poll_responses").delete().eq("poll_id", pollId);
        if (error) throw error;

        setResponseCount(0);
        toast.success("Poll responses wiped successfully");
      } else {
        await supabase.from("polls").delete().eq("id", pollId);
        setPollId(null);
        setClusters([]);
        setRankingOptions([]);
        setActivePollType("constant_sum_two_level");
        setSavedConfigurations({});
        setHideZeroAllocated(false);
        setMinimumRequiredRanks(3);
        setResultViews({ ...defaultResultViews });
        setResponseCount(0);
        toast.success("Poll setup wiped successfully");
      }
      return true;
    } catch (error) {
      console.error("Error wiping poll:", error);
      toast.error("Failed to wipe poll data");
      return false;
    } finally {
      setLoading(false);
    }
  }, [
    pollId,
    responseCount,
    setLoading,
    setResponseCount,
    setPollId,
    setClusters,
    setRankingOptions,
    setActivePollType,
    setSavedConfigurations,
    setHideZeroAllocated,
    setMinimumRequiredRanks,
    setResultViews,
  ]);

  return {
    loadPoll,
    savePoll,
    wipePollData,
  };
};
